import React from 'react';
import { motion } from 'framer-motion';

/**
 * StudioScenarios — "Diferentes cenários. Uma mesma essência."
 * Mostra conceitos visuais dos ambientes. NUNCA apresenta como fotos reais do
 * espaço já construído — cada card leva o selo "CONCEITO VISUAL".
 */
const scenarios = [
  {
    title: 'Cenário Podcast Premium',
    desc: 'Iluminação cinematográfica, mesa de madeira, poltronas de couro e microfones profissionais Shure.',
    img: '/scenarios/podcast-premium.jpg',
  },
  {
    title: 'Cenário de Entrevista',
    desc: 'Poltronas de veludo, luz âmbar quente e tijolinho escuro — foco no diálogo.',
    img: '/scenarios/entrevistas.jpg',
  },
  {
    title: 'Ambiente Fotográfico',
    desc: 'Fundo infinito branco, softbox âmbar, câmera cinema em tripé e piso reflexivo.',
    img: '/scenarios/fotografia.jpg',
  },
  {
    title: 'Produção de Conteúdo',
    desc: 'Cenário com plantas tropicais, neon teal, ring light e sofá — reels, cortes e séries.',
    img: '/scenarios/conteudo.jpg',
  },
  {
    title: 'Cenários Personalizados',
    desc: 'Cortinas veludo com feixe de spot âmbar cortando a escuridão — mood teatral.',
    img: '/scenarios/personalizados.jpg',
  },
  {
    title: 'Institucional / Corporativo',
    desc: 'Fundo minimalista, luz difusa suave, ambiente clean para vídeos corporativos.',
    img: '/scenarios/institucional.jpg',
  },
];

const StudioScenarios = () => {
  return (
    <section
      id="cenarios"
      data-testid="studio-scenarios"
      className="relative py-24 md:py-32 bg-[#050508] border-t border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(37,64,163,0.10)_0%,rgba(0,0,0,0)_55%)]" />
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14 md:mb-16 max-w-3xl mx-auto"
        >
          <span className="text-[10px] md:text-xs uppercase tracking-[0.4em] font-bold text-[#F59E0B] mb-4 inline-block">
            Conceitos Visuais
          </span>
          <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-black text-[#F5F5F0] leading-[1.1] mb-6">
            Diferentes cenários.{' '}
            <span className="text-[#2540A3] italic font-black">
              Uma mesma essência.
            </span>
          </h2>
          <p className="text-sm md:text-base text-[#8A8A8A] font-light leading-relaxed">
            Imagens abaixo são <strong className="text-[#F5F5F0]">visualizações fotorrealistas</strong> do
            projeto — mood, iluminação e ambientação do que será o espaço. Não são fotos oficiais do
            estúdio, que ainda está em desenvolvimento.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {scenarios.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.5, delay: Math.min(i * 0.06, 0.4) }}
              data-testid={`scenario-card-${i}`}
              className="group relative overflow-hidden rounded-2xl border border-white/8 hover:border-[#2540A3]/50 bg-[#0B0B0F] transition-all duration-500"
            >
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={s.img}
                  alt={s.title}
                  loading="lazy"
                  className="w-full h-full object-cover brightness-95 group-hover:brightness-100 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0F] via-[#0B0B0F]/40 to-transparent" />
                {/* concept badge */}
                <span className="absolute top-3 left-3 text-[9px] uppercase tracking-[0.3em] font-bold text-[#F59E0B] bg-[#0B0B0F]/70 backdrop-blur-sm border border-[#F59E0B]/40 px-2 py-1 rounded">
                  Conceito Visual
                </span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3 className="font-display text-base md:text-lg font-bold text-[#F5F5F0] mb-1 leading-tight">
                  {s.title}
                </h3>
                <p className="text-xs md:text-sm text-[#A1A1AA] font-light leading-relaxed">
                  {s.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StudioScenarios;
